/**
 * BadgeCard Component
 *
 * Displays a single badge with hover/focus tooltip showing details.
 * Includes full keyboard navigation and screen reader support.
 */

import { useState } from 'react'
import PropTypes from 'prop-types'

const RARITY_COLORS = {
  COMMON: 'from-gray-400 to-gray-500',
  UNCOMMON: 'from-green-400 to-green-600',
  RARE: 'from-blue-400 to-blue-600',
  EPIC: 'from-purple-400 to-purple-600',
  LEGENDARY: 'from-yellow-400 via-orange-500 to-red-500'
}

const RARITY_TEXT_COLORS = {
  COMMON: 'text-gray-400',
  UNCOMMON: 'text-green-400',
  RARE: 'text-blue-400',
  EPIC: 'text-purple-400',
  LEGENDARY: 'text-yellow-400'
}

// Rarity patterns for non-color-only differentiation (accessibility)
const RARITY_PATTERNS = {
  COMMON: '',
  UNCOMMON: 'border-2 border-dashed border-white/30',
  RARE: 'border-2 border-dotted border-white/40',
  EPIC: 'border-2 border-solid border-white/50',
  LEGENDARY: 'ring-2 ring-white/60 ring-offset-2 ring-offset-transparent'
}

const BadgeCard = ({ badge = null, earnedAt = null, locked = false, onClick = null }) => {
  const [showTooltip, setShowTooltip] = useState(false)

  if (!badge) return null

  const rarityColor = RARITY_COLORS[badge.rarity] || RARITY_COLORS.COMMON
  const rarityTextColor = RARITY_TEXT_COLORS[badge.rarity] || RARITY_TEXT_COLORS.COMMON
  const rarityPattern = RARITY_PATTERNS[badge.rarity] || ''

  // Build accessible label
  const statusText = locked
    ? 'Not yet earned'
    : earnedAt
      ? `Earned ${new Date(earnedAt).toLocaleDateString()}`
      : 'Earned'
  const ariaLabel = `${badge.name} badge, ${badge.rarity?.toLowerCase() || 'common'} rarity, ${badge.pointsValue} points. ${statusText}`

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (onClick) onClick(badge)
      setShowTooltip((prev) => !prev)
    }
    if (e.key === 'Escape') {
      setShowTooltip(false)
    }
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={showTooltip}
        aria-describedby={showTooltip ? `badge-tooltip-${badge.code}` : undefined}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        onClick={() => onClick?.(badge)}
        className={`
          relative overflow-hidden rounded-lg p-4 text-center cursor-pointer
          transform transition-transform hover:scale-105
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
          ${locked ? 'opacity-50 grayscale' : ''}
          bg-gradient-to-br ${rarityColor}
          ${rarityPattern}
        `}
      >
        {/* Badge Image/Icon */}
        <div className="w-16 h-16 mx-auto mb-2">
          {badge.iconPath ? (
            <img
              src={badge.iconPath}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={`w-full h-full rounded-full bg-white/20 items-center justify-center ${badge.iconPath ? 'hidden' : 'flex'}`}
            aria-hidden="true"
          >
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>

        {/* Badge Name */}
        <p className="text-white font-medium text-sm truncate">{badge.name}</p>

        {/* Rarity Text Label (for accessibility - not color-only) */}
        <span className="sr-only">{badge.rarity?.toLowerCase()} rarity</span>

        {/* Rarity Indicator Dot */}
        <span
          className="absolute top-1 right-1 w-2 h-2 rounded-full bg-white/50"
          aria-hidden="true"
        ></span>

        {/* Locked Overlay */}
        {locked && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg"
            aria-hidden="true"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          id={`badge-tooltip-${badge.code}`}
          role="tooltip"
          className="absolute z-50 w-48 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full pointer-events-none"
        >
          <p className="font-semibold mb-1">{badge.name}</p>
          <p className="text-gray-300 text-xs mb-2">{badge.description}</p>
          <div className="flex justify-between text-xs text-gray-400">
            <span className={`capitalize ${rarityTextColor}`}>{badge.rarity?.toLowerCase()}</span>
            <span>+{badge.pointsValue} pts</span>
          </div>
          {earnedAt && (
            <p className="text-xs text-gray-400 mt-1">
              Earned {new Date(earnedAt).toLocaleDateString()}
            </p>
          )}
          {locked && <p className="text-xs text-yellow-400 mt-1">Not yet earned</p>}
          {/* Tooltip Arrow */}
          <div
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"
            aria-hidden="true"
          ></div>
        </div>
      )}
    </div>
  )
}

BadgeCard.propTypes = {
  badge: PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    rarity: PropTypes.oneOf(['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY']),
    pointsValue: PropTypes.number,
    iconPath: PropTypes.string
  }),
  earnedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  locked: PropTypes.bool,
  onClick: PropTypes.func
}

export default BadgeCard
